import { Module, Mutation, Action } from 'vuex-module-decorators'
import {
  defaultSystemCode,
  defaultSystemParameter,
  GetSystemParameterRequest,
  SystemCode,
  SystemParameter,
  systemParameterApi
} from '@/api'
import BaseStore from '@/store/BaseStore'
import CODE_KEY from '@/config/code_key'
import {
  authUserStore,
  dataStore,
  systemCodeStore
} from '@/utils/store-accessor'
import { ReserveMethod } from '@/types'

@Module({
  name: 'SystemParameterStore',
  namespaced: true,
  stateFactory: true
})
export default class SystemParameterStore extends BaseStore {
  items: SystemParameter[] = []

  get allItems() {
    return this.items
  }

  get findByCodeKey(): (codeKey: string) => SystemParameter | undefined {
    return (codeKey: string) => {
      return this.items.find(item => item.codeKey === codeKey)
    }
  }

  get filterByCodeKey(): (codeKey: string) => SystemParameter[] {
    return (codeKey: string) => {
      return this.items.filter(item => item.codeKey === codeKey)
    }
  }

  get findByCodeKeyAndCode(): (
    codeKey: string,
    code?: string
  ) => SystemParameter | undefined {
    return (codeKey: string, code?: string) => {
      return this.items.find(item => {
        if (code === undefined) {
          return item.codeKey === codeKey
        }
        return item.codeKey === codeKey && item.code === code
      })
    }
  }

  get oneHitTransitionTargetSettingOnSearch(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.ONE_HIT_TRANSITION_TARGET_SETTING_ON_SEARCH.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()
    )
  }

  // option1 0:氏名漢字，1:氏名カナ，2:年号，3:生年月日，4:性別，5:電話番号，6:住所コード，7:郵便番号，8:地区コード，9:住所
  get userRegistrationUpdateRequiredItemSetting(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.USER_REGISTRATION_UPDATE_REQUIRED_ITEM_SETTING.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()
    )
  }

  get userNewRegistrationDefaultValueSetting(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.USER_NEW_REGISTRATION_DEFAULT_VALUE_SETTING.KEY
      ) ?? defaultSystemParameter()
    )
  }

  get userOpacPassword(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.USER_OPAC_PASSWORD_SETTING.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()
    )
  }

  get presetValueSettingOnSearch1(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.PRESET_VALUE_SETTING_ON_SEARCH1.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()
    )
  }

  get presetValueSettingOnSearch2(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.PRESET_VALUE_SETTING_ON_SEARCH2.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()
    )
  }

  get defaultCollectionStatusRemove(): boolean {
    return this.presetValueSettingOnSearch2.option5 !== '0'
  }

  get returnHistorySetting(): SystemParameter {
    return (
      this.findByCodeKeyAndCode(
        CODE_KEY.RETURN_HISTORY_SETTING.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()
    )
  }

  get userIdMaxDigits(): number {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.NUMBER_MANAGEMENT.KEY,
      '01'
    )
    if (parameter === undefined) {
      return 5
    }
    return Number(parameter.option1)
  }

  get userIdMask(): string {
    return '#'.repeat(this.userIdMaxDigits)
  }

  get userIdMaskByAlphaNumHalf(): string {
    return 'X'.repeat(this.userIdMaxDigits)
  }

  get isCollectionNoMaxDigitsMutable(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(CODE_KEY.NUMBER_MANAGEMENT.KEY, '02') ??
      defaultSystemParameter()

    return parameter.option1 === 'NN'
  }

  // TODO 後々画面側でも使うかもしれないのでコメントアウト
  // get collectionNoMutableCorrectDigits(): number[] {
  //   const parameter =
  //     this.findByCodeKeyAndCode(CODE_KEY.NUMBER_MANAGEMENT.KEY, '02') ??
  //     defaultSystemParameter()
  //
  //   if (parameter.option1 !== 'NN') {
  //     return []
  //   }
  //
  //   return parameter.option5.split(',').map(Number)
  // }

  // TODO 後々画面側でも使うかもしれないのでコメントアウト
  // get existsUserIdMaxDigitsInCollectionNoMutableCorrectDigits(): boolean {
  //   return this.collectionNoMutableCorrectDigits.some(
  //     value => value === this.userIdMaxDigits
  //   )
  // }

  get collectionNoMaxDigits(): number {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.NUMBER_MANAGEMENT.KEY,
      '02'
    )
    if (parameter === undefined) {
      return 5
    }

    return parameter.option1 === 'NN' ? 16 : Number(parameter.option1)
  }

  // 基本的にはこちらを使用
  get collectionNoMask(): string {
    return '#'.repeat(this.collectionNoMaxDigits)
  }

  // コードミスではない、Dから始まる資料番号が混在している為、状況に応じて使用している
  get collectionNoMaskByAlphaNumHalf(): string {
    return 'X'.repeat(this.collectionNoMaxDigits)
  }

  get forwardingRequestEnable(): boolean {
    const parameter = this.findByCodeKey(
      CODE_KEY.RESERVE_FORWARDING_ENABLED_SETTING.KEY
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 === '1'
  }

  get defaultForwardingRequestBranchCodes(): string[] {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.DEFAULT_FORWARDING_REQUEST_BRANCH.KEY,
      authUserStore.authUser.branchCode.code
    )
    if (parameter === undefined) {
      return []
    }

    const branchCodes = parameter.option1.match(/.{2}/g)
    if (branchCodes === null) {
      return []
    }

    return branchCodes
  }

  get isDisplayReturnHistoryButton(): boolean {
    const parameter = this.findByCodeKey(
      CODE_KEY.RETURN_HISTORY_DISPLAY_ENABLED_SETTING.KEY
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 === '1'
  }

  get callNumberConcatenationNumber(): string {
    const parameter = this.findByCodeKey(
      CODE_KEY.CALL_CONCATENATION_NUMBER_INSTRUCTION.KEY
    )
    if (parameter === undefined) {
      return ''
    }
    return parameter.option1
  }

  get searchResultDetailSubTitle(): string {
    const parameter = this.findByCodeKey(
      CODE_KEY.DETAIL_LIST_DISPLAY_SETTING_3.KEY
    )
    if (parameter === undefined) {
      return ''
    }
    return parameter.option2
  }

  get inspectionMaxNumber(): string {
    const parameter = this.findByCodeKey(CODE_KEY.INSPECTION_MAX_NUMBER.KEY)

    if (parameter === undefined) {
      return ''
    }
    return parameter.option1
  }

  get isReturnProcessBulk(): boolean {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.PROCESSING_METHOD_SETTING.KEY,
      authUserStore.authUser.branchCode.code
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 === '1'
  }

  get isSetCallNumberOnRegistration(): boolean {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.CALL_NUMBER_SETTING.KEY,
      '3'
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 !== '1'
  }

  get displayModalSelectReserveType(): boolean {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.RESERVE_TYPE_SELECT_DISPLAY_ENABLED.KEY,
      ''
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 === '1'
  }

  get isManagementFamilyHomePhoneNumber(): boolean {
    const parameter = this.findByCodeKey(
      CODE_KEY.FAMILY_MANAGEMENT_BASIC_NUMBER_SETTING.KEY
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 === '2' || parameter.option1 === '4'
  }

  get isManagementFamily(): boolean {
    const parameter: SystemParameter =
      this.findByCodeKey(CODE_KEY.FAMILY_MANAGEMENT_BASIC_NUMBER_SETTING.KEY) ??
      defaultSystemParameter()

    return parameter.option1 !== '0'
  }

  // 現状option1しか使用しないのでこの値を用いることにする
  get isFocusNameUserSearchPage(): boolean {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.USER_SEARCH_PAGE_INITIAL_FOCUS.KEY,
      authUserStore.authUser.branchCode.code
    )
    if (parameter === undefined) {
      return false
    }
    return parameter.option1 === '1'
  }

  get reserveNoticeDefaultValue(): string {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.RESERVE_CONTACT_METHOD_DEFAULT_VALUE_SETTING.KEY,
      authUserStore.authUser.branchCode.code
    )
    if (parameter === undefined) {
      return ''
    }
    return parameter.option1
  }

  get contactMethodDefaultValue(): string {
    const parameter = this.findByCodeKeyAndCode(
      CODE_KEY.RESERVE_CONTACT_METHOD_DEFAULT_VALUE_SETTING.KEY,
      authUserStore.authUser.branchCode.code
    )
    if (parameter === undefined) {
      return ''
    }
    return parameter.option2
  }

  get defaultCollectionSearchTitleInformationValue(): boolean {
    return this.presetValueSettingOnSearch1.option6 === '1'
  }

  get disabledReminderMethod(): boolean {
    const parameter =
      this.findByCodeKey(CODE_KEY.REMINDER_METHOD_SETTING.KEY) ??
      defaultSystemParameter()

    return parameter.option1 !== '1'
  }

  get defaultReminderMethodValue(): number {
    const parameter =
      this.findByCodeKey(CODE_KEY.REMINDER_METHOD_SETTING.KEY) ??
      defaultSystemParameter()

    switch (parameter?.option2 ?? '') {
      case '0':
        return 1
      case '1':
        return 2
      case '2':
        return 3
      default:
        return 1
    }
  }

  // systemCodeKey「C0048」を使用している為、systemCode取得後でなければ機能しないので注意
  get defaultReminderTypeValue(): string {
    const parameter =
      this.findByCodeKey(CODE_KEY.REMINDER_METHOD_SETTING.KEY) ??
      defaultSystemParameter()

    return systemCodeStore
      .filterByCodeKey(CODE_KEY.REMINDER_TYPE_SETTING.KEY)
      .some(systemCode => systemCode.code === parameter.option3)
      ? parameter.option3
      : ''
  }

  get usingAppendixLendManagement(): boolean {
    const parameter =
      this.findByCodeKey(
        CODE_KEY.APPENDIX_LEND_MANAGEMENT_USING_ENABLED_SETTING.KEY
      ) ?? defaultSystemParameter()

    return parameter.option1 === '1' || parameter.option1 === '2'
  }

  get usingAppendixLendManagementAsCollection(): boolean {
    const parameter =
      this.findByCodeKey(
        CODE_KEY.APPENDIX_LEND_MANAGEMENT_USING_ENABLED_SETTING.KEY
      ) ?? defaultSystemParameter()

    return parameter.option1 === '2'
  }

  get isDisplayAlertTitleOnLendReturn(): boolean {
    const parameter =
      this.findByCodeKey(CODE_KEY.LEND_RETURN_MESSAGE_OUTPUT_SETTING.KEY) ??
      defaultSystemParameter()

    return parameter.option1 === '1'
  }

  get isDisplayMailSendHistory(): boolean {
    const parameter =
      this.findByCodeKey(CODE_KEY.MAIL_HISTORY_SETTING.KEY) ??
      defaultSystemParameter()

    return parameter.option1 === '1'
  }

  // ネクシモ版では常に必須だが後々変更する可能性もありえるのでstore上で管理とする
  get isRequireUserName(): boolean {
    return true
  }

  get isRequireUserNameKana(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[1] === '1'
  }

  get isRequireUserBirthday(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[3] === '1'
  }

  get isRequireUserGender(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[4] === '1'
  }

  get isRequireUserPhone(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[5] === '1'
  }

  get isRequireUserAddressCode(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[6] === '1'
  }

  get isRequireUserZipcode(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[7] === '1'
  }

  get isRequireUserAreaCode(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[8] === '1'
  }

  get isRequireUserAddress(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option1[9] === '1'
  }

  get isRequireUserNameKanaBySimpleRegistration(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option2[1] === '1'
  }

  get isRequireUserBirthdayBySimpleRegistration(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option2[3] === '1'
  }

  get isRequireUserGenderBySimpleRegistration(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option2[4] === '1'
  }

  get isRequireUserPhoneBySimpleRegistration(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option2[5] === '1'
  }

  get isRequireUserAddressCodeBySimpleRegistration(): boolean {
    return this.userRegistrationUpdateRequiredItemSetting.option2[6] === '1'
  }

  get displayLendShortReceipt(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING1.KEY,
        authUserStore.authUser.branchCode.code + '10'
      ) ?? defaultSystemParameter()

    return parameter.option2 === '1' || parameter.option2 === '2'
  }

  get defaultCheckLendShortReceipt(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING1.KEY,
        authUserStore.authUser.branchCode.code + '10'
      ) ?? defaultSystemParameter()

    return parameter.option2 === '2'
  }

  get displayLendShortReceiptByGroupUser(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING1.KEY,
        authUserStore.authUser.branchCode.code + '35'
      ) ?? defaultSystemParameter()

    return parameter.option2 === '1' || parameter.option2 === '2'
  }

  get defaultCheckLendShortReceiptByGroupUser(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING1.KEY,
        authUserStore.authUser.branchCode.code + '35'
      ) ?? defaultSystemParameter()

    return parameter.option2 === '2'
  }

  get displayLendShortReceiptByReadingInside(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING1.KEY,
        authUserStore.authUser.branchCode.code + '50'
      ) ?? defaultSystemParameter()

    return parameter.option2 === '1' || parameter.option2 === '2'
  }

  get defaultCheckLendShortReceiptByReadingInside(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING1.KEY,
        authUserStore.authUser.branchCode.code + '50'
      ) ?? defaultSystemParameter()

    return parameter.option2 === '2'
  }

  get bulkPrintingOfReceiptLendPrint(): boolean {
    const parameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_RECEIPT_OUTPUT_DEFAULT_SETTING2.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemParameter()

    return parameter.option1 === '1' && dataStore.receiptInfoLendBulkPrint
  }

  get isDisplayOrderListCollectionStatusAllBranch(): boolean {
    const parameter =
      this.findByCodeKey(CODE_KEY.ORDER_LIST_EDIT_DISPLAY_SETTING.KEY) ??
      defaultSystemParameter()

    return parameter.option4 !== '1'
  }

  get isLendBulk(): (lendType: string) => boolean {
    return (lendType: string) => {
      const parameter: SystemParameter =
        this.findByCodeKeyAndCode(
          CODE_KEY.LEND_METHOD_SETTING.KEY,
          authUserStore.authUser.branchCode.code + lendType
        ) ?? defaultSystemParameter()

      return parameter.option1 === '1'
    }
  }

  get displayNdc(): (ndc8: string, ndc9: string, ndc10: string) => string {
    return (ndc8: string, ndc9: string, ndc10: string) => {
      const parameter: SystemParameter =
        this.findByCodeKey(
          CODE_KEY.TOOLI_BIBLIOGRAPHY_REGISTER_PARAMETER2.KEY
        ) ?? defaultSystemParameter()

      switch (parameter.option1) {
        case '1':
          return ndc8
        case '2':
          return ndc9
        case '3':
          return ndc10
        default:
          return ndc10
      }
    }
  }

  get isUpdateReservePreparation(): boolean {
    const parameter: SystemParameter =
      this.findByCodeKey(CODE_KEY.RETURNED_RESERVE_STATUS_SETTING.KEY) ??
      defaultSystemParameter()

    return parameter.option3 === '1'
  }

  get mobileLibraryPlaceCodes(): (branchCode: string) => SystemCode[] {
    return (branchCode: string) => {
      const parameter: SystemParameter =
        this.findByCodeKeyAndCode(
          CODE_KEY.MOBILE_LIBRARY_PROCESSING_PLACE.KEY,
          branchCode
        ) ?? defaultSystemCode()
      const systemCodes: SystemCode[] = systemCodeStore.filterByCodeKey(
        CODE_KEY.PROCESSING_PLACE.KEY
      )

      return systemCodes.filter(systemCode => {
        const splitArray = parameter.option1.match(/.{2}/g) || []
        return splitArray.some(placeCode => placeCode === systemCode.code)
      })
    }
  }

  get isPriorityDisplayHandlingTypeLendOutTab(): boolean {
    const parameter: SystemParameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_OUT_TAB_DISPLAY_ITEM_SETTING.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemCode()

    return parameter.option1 === '1'
  }

  get isDisplayLendBranchToCollectionStatusColumn(): boolean {
    const parameter: SystemParameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.LEND_OUT_COLLECTION_DISPLAY_SETTING.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemCode()

    return parameter.option2 !== '1'
  }

  get reserveMethodPresetValue(): ReserveMethod {
    const parameter: SystemParameter =
      this.findByCodeKey(CODE_KEY.RESERVE_METHOD_PRESET_SETTING.KEY) ??
      defaultSystemCode()

    return parameter.option1 === '0'
      ? ReserveMethod.SpecifyBranch
      : ReserveMethod.AllBranch
  }

  get defaultReserveSpecifyBranchCodes(): SystemCode[] {
    const parameter: SystemParameter =
      this.findByCodeKeyAndCode(
        CODE_KEY.RESERVE_SPECIFY_BRANCH_CODE_DEFAULT_SETTING.KEY,
        authUserStore.authUser.branchCode.code
      ) ?? defaultSystemCode()

    if (parameter.option1 === '') {
      return []
    }

    return parameter.option1.split(',').map(code => {
      const systemCode:
        | SystemCode
        | undefined = systemCodeStore.getLibraryBranch.find(
        item => item.code === code
      )

      return (
        systemCode ??
        Object.assign(defaultSystemCode(), {
          codeKey: CODE_KEY.LIBRARY_BRANCH.KEY,
          code
        })
      )
    })
  }

  @Mutation
  public setItems(systemParameters: SystemParameter[] = []) {
    this.items = systemParameters
  }

  @Mutation
  public mergeItems(systemParameters: SystemParameter[] = []) {
    const addKeys: string[] = []
    systemParameters.forEach(item => {
      if (!addKeys.includes(item.codeKey)) addKeys.push(item.codeKey)
    })
    this.items = this.items.filter(item => {
      if (!addKeys.includes(item.codeKey)) {
        return item
      }
    })
    systemParameters.forEach(item => {
      this.items.push(item)
    })
  }

  @Action({ rawError: true })
  public async fetchAllByCodeKeys(
    codeKeys: string[]
  ): Promise<SystemParameter[]> {
    if (codeKeys.length === 0) {
      this.setItems([])
      return []
    }

    const systemParameterRequest: GetSystemParameterRequest = {
      codeKey: '',
      codeKeys,
      code: ''
    }

    const systemParameters = await systemParameterApi.getSystemParameters(
      systemParameterRequest
    )

    this.setItems(systemParameters)

    return systemParameters
  }

  @Action({ rawError: true })
  public async fetchAllAndMergeByCodeKeys(
    codeKeys: string[]
  ): Promise<SystemParameter[]> {
    if (codeKeys.length === 0) {
      return []
    }

    const systemParameterRequest: GetSystemParameterRequest = {
      codeKey: '',
      codeKeys,
      code: ''
    }

    const systemParameters = await systemParameterApi.getSystemParameters(
      systemParameterRequest
    )

    this.mergeItems(systemParameters)

    return systemParameters
  }
}
